import React, { FC, FormEvent, useEffect, useState } from "react";
import {
  DatePicker,
  Label,
  MaskedTextField,
  mergeStyleSets,
  Stack,
  Modal,
  PrimaryButton,
  DefaultButton,
  IconButton,
  getTheme,
  MessageBar,
  MessageBarType
} from "@fluentui/react";
import { Patch } from "../../api/apiUtils";
import { LineHandlingApi } from "../../api/lineHandlingApi";
import moment from "moment";
import { ErrorResponse, LineHandling } from "../../models";
import { last6Digits } from "../../services/Helper";
import "./Eta.css";

type Props = {
  lineHandling: LineHandling;
  onSuccess(value: string): void;
  onClose(): void;
};
const classes = mergeStyleSets({
  container: {
    padding: 35,
    width: 500,
    borderRadius: 8,
  },
  header: {
    display: "flex",
    flex: "1 1 auto"
  },
  footer: {
    marginTop: 15,
    display: "flex",
    justifyContent: "space-between"
  },
  title: {
    fontWeight: 500,
    fontSize: 20,
    lineHeight: 23,
    alignItems: "center",
    letterSpacing: "0.0015em",
    color: "#12427D"
  },
  buttons: {
    borderRadius: 8,
    height: 52,
    width: 210
  },
  buttonWide: {
    borderRadius: 8,
    width: "100%",
    height: 50,
    backgroundColor: "#FFF",
    border: "1px solid #262626",
  },
  inputField: {
    width:210,
    fontSize: "22px !important",
  },
  calendar: {
    fontSize: 15
  }
});
const theme = getTheme();
const iconButtonStyles = {
  root: {
    color: theme.palette.neutralPrimary,
    marginLeft: "auto",
    marginTop: "4px",
    marginRight: "2px"
  },
  rootHovered: {
    color: theme.palette.neutralDark
  }
};

const EtaModal: FC<Props> = (props: Props) => {
  const [value, setValue] = useState(new Date());
  const [timeValue, setTimeValue] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupStatus, setLookupStatus] = useState(0);
  const [progress, setProgress] = useState(0);

  const saveEtaAsync = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isValid() && timeValue) {
      let hour = parseInt(timeValue.substr(0, 2));
      let minute = parseInt(timeValue.substr(3, 2));
      let etaVal = value;
      etaVal.setHours(hour);
      etaVal.setMinutes(minute);
      setIsLoading(true);

      const response = await Patch(
        `/api/linehandling/${props.lineHandling.lineHandlingNo}`,
        JSON.stringify([
          {
            op: "replace",
            path: "/eta",
            value: etaVal.toISOString()
          }
        ])
      );
      setIsLoading(false);
      if (response.ok) {
        const result = (await response.json()) as LineHandling;
        props.onSuccess(result.eta);
      } else {
        const result = (await response.json()) as ErrorResponse;
        setErrorMessage(result.message);
      }
    }
  };

  const isValid = (): boolean => {
    const timeEx = new RegExp(/^(?:2[0-3]|[01][0-9]):[0-5][0-9]$/);
    if (timeValue && timeEx.test(timeValue)) {
      return true;
    }
    
    setErrorMessage("Invalid Time value");
    return false;
  };

  const onLookup = async() => {
    setIsLookingUp(true);
    setIsLoading(true);
    //loading simulation takes 3000ms
    let res = await LineHandlingApi.generateEtaAsync(props?.lineHandling.lineHandlingNo);
    setTimeout(()=>{
      if(res.eta){
        setLookupStatus(1);
        let eta = ((res?.eta).split(" "));
        setValue(new Date(eta[0]));
        setTimeValue(eta[1].substring(0,5));
      }else{
        setLookupStatus(1);
      }
      setProgress(100);
    }, 3000);
    setTimeout(()=>{
      setProgress(0);
      setLookupStatus(0);
      setIsLoading(false);
      setIsLookingUp(false);
    }, 4000);
  };

  useEffect(() => {
    if (props.lineHandling.eta) {
      const local = moment.utc(`${props.lineHandling.eta} UTC`).local();
      setValue(local.toDate());
      setTimeValue(local.format("HH:mm"));
    }
  }, [props.lineHandling]);

  return (
    <Modal
      titleAriaId="title"
      isOpen={true}
      onDismiss={props.onClose}
      isBlocking={false}
      containerClassName={classes.container}
    >
      <form onSubmit={saveEtaAsync}>
        <div className={classes.header}>
          <h6>{!isLookingUp?"Estimated Arrival":"Find ETA"}</h6>
          <IconButton
            id="btn-close"
            styles={iconButtonStyles}
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close popup modal"
            onClick={props.onClose}
            disabled={isLoading}
          />
        </div>
        {!isLookingUp?(
        <div>
        <h5>Need to update ETA?</h5>
        <p>Edit it manually or let the system find it for you automatically.</p>
          <Stack className="modal-inputs">
            {errorMessage && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
                onDismiss={() => setErrorMessage(undefined)}
                dismissButtonAriaLabel="Close"
              >
                {errorMessage}
              </MessageBar>
            )}
            <DatePicker
              id="txt-eta-date"
              label="Date"
              className={classes.inputField}
              formatDate={(val?: Date) => (val ? moment(val).format("ll") : "")}
              onSelectDate={(date: Date | null | undefined) =>
                date && setValue(date)
              }
              disabled={isLoading}
              value={value}
              minDate={new Date()}
            />
            <MaskedTextField
              id="txt-eta-time"
              label="Time"
              className={classes.inputField}
              mask="12:32"
              maskFormat={{
                "1": /[0-2]/,
                "2": /[0-9]/,
                "3": /[0-5]/
              }}
              disabled={isLoading}
              onChange={(e, value?: string) => setTimeValue(value)}
              value={timeValue}
            />
          </Stack>
          <DefaultButton
            id="btn-cancel"
            className={classes.buttonWide}
            onClick={onLookup}
            disabled={isLoading}
          >
            <img src={"/misc/globe_search.svg"}/>
            Find ETA
          </DefaultButton>
        </div>
        ):(
          <div className="eta-lookup-content">
            <h6>ETA Lookup {lookupStatus==0?"in Progress":lookupStatus==1?"Successful!":"Failed!"} </h6>
            <div className="vessel-circle" onClick={()=>setLookupStatus(Math.floor(Math.random() * 3))}>
              <div className={`progress ${lookupStatus==2?'red':'green'}`}>
                 <span className="progress-left">
                                   <span className="progress-bar"></span>
                 </span>
                 <span className="progress-right">
                                   <span className="progress-bar"></span>
                 </span>
              <div className="icon" style={{backgroundColor: lookupStatus==0?"#f5f5f5":lookupStatus==1?"#15803D":"#B91C1C"}}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="40"
                  viewBox="0 -960 960 960"
                  width="40"
                  style={{filter: lookupStatus==0?"none":"invert(100%) sepia(0%) saturate(0%) hue-rotate(287deg) brightness(108%) contrast(101%)"}}
                  >
                  <path d="M479.769-418Zm-291 176.462L131.23-452.615q-2.23-9.693 1.731-21.077 3.962-11.385 15.039-15.077l52-18V-680q0-27.616 18.5-46.116t46.115-18.5H400v-120h160v120h135.385q27.615 0 46.115 18.5Q760-707.616 760-680v173.231l52 18q13.308 4.923 18.077 15.807 4.769 10.885 1.769 20.347l-60.615 211.077Q735.077-253.077 706-275.423 676.923-297.769 640-340q-34.615 41.462-72.154 70.731Q530.308-240 480-240q-50.308 0-87.846-29.269Q354.615-298.538 320-340q-36.154 42.231-65.615 64.577-29.462 22.346-65.616 33.885ZM99.999-80v-40H160q42 0 83-13t77-39q36 26 77 38t83 12q42 0 83-12t77-38q36 26 77 39t83 13h60v40h-60q-42 0-82-10t-78-30q-38 20-78.5 30T480-80q-41 0-81.5-10T320-120q-38 20-78 30t-82 10h-60ZM240-520.462l240-78 240 78V-680q0-10.769-6.923-17.692t-17.692-6.923h-430.77q-10.769 0-17.692 6.923T240-680v159.538ZM480-280q47 0 78.731-32.615Q590.462-345.231 640-402q47.231 54 64.615 73.231Q722-309.538 746-295.538L790-454 480-556 169.538-454 214-296q24-14 41.385-32 17.384-18 64.615-74 50 57 81.5 89.5T480-280Z" />
                </svg>
              </div>
                    </div>
            </div>
            <h6>{props?.lineHandling.vesselName}</h6>
            <p>{props?.lineHandling.lineHandlingNo}</p>
          </div>
        )}
        <div className={classes.footer}>
          <PrimaryButton
            id="btn-submit"
            className={classes.buttons}
            disabled={isLoading}
            type="submit"
          >
            Confirm
          </PrimaryButton>
          <DefaultButton
            id="btn-cancel"
            className={classes.buttons}
            onClick={props.onClose}
            disabled={isLoading}
          >
            Cancel
          </DefaultButton>
        </div>
      </form>
    </Modal>
  );
};

export default EtaModal;
